import { useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, IconButton,
  Menu, MenuItem, Paper, Snackbar, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { Add as AddIcon, MoreVert } from '@mui/icons-material';
import { LabFormDialog } from './LabFormDialog';
import {
  useLabsQuery, useCreateLabMutation,
  useUpdateLabMutation, useDeactivateLabMutation,
} from './hooks/useLabsQuery';
import type { Lab } from '../../services/labService';

export function LabsPage() {
  const { data: labs, isLoading, isError, error } = useLabsQuery();
  const createMutation = useCreateLabMutation();
  const updateMutation = useUpdateLabMutation();
  const deactivateMutation = useDeactivateLabMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Lab | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Lab | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; lab: Lab } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const showMessage = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleCreate = async (data: { name: string; country?: string; timeZone?: string }) => {
    try {
      await createMutation.mutateAsync(data);
      setCreateOpen(false);
      showMessage('Lab created successfully');
    } catch {
      showMessage('Failed to create lab', 'error');
    }
  };

  const handleUpdate = async (data: { name: string; country?: string; timeZone?: string }) => {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ id: editTarget.id, data });
      setEditTarget(null);
      showMessage('Lab updated successfully');
    } catch {
      showMessage('Failed to update lab', 'error');
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    try {
      await deactivateMutation.mutateAsync(deactivateTarget.id);
      setDeactivateTarget(null);
      showMessage(`"${deactivateTarget.name}" deactivated`);
    } catch {
      showMessage('Failed to deactivate lab', 'error');
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>Lab Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Create Lab
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">Failed to load labs: {error instanceof Error ? error.message : 'Unknown error'}</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Timezone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {labs?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No labs found. Create one to get started.
                  </TableCell>
                </TableRow>
              )}
              {labs?.map((lab) => (
                <TableRow key={lab.id}>
                  <TableCell>{lab.name}</TableCell>
                  <TableCell>{lab.country ?? '—'}</TableCell>
                  <TableCell>{lab.timeZone ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={lab.status === 'active' ? 'Active' : 'Inactive'}
                      color={lab.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => setMenuAnchor({ el: e.currentTarget, lab })}
                      aria-label={`Actions for ${lab.name}`}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Row actions menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => { setEditTarget(menuAnchor!.lab); setMenuAnchor(null); }}>
          Edit
        </MenuItem>
        {menuAnchor?.lab.status === 'active' && (
          <MenuItem
            onClick={() => { setDeactivateTarget(menuAnchor!.lab); setMenuAnchor(null); }}
            sx={{ color: 'error.main' }}
          >
            Deactivate
          </MenuItem>
        )}
      </Menu>

      {/* Create dialog */}
      <LabFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create Lab"
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      {/* Edit dialog */}
      <LabFormDialog
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        title="Edit Lab"
        initialData={editTarget ? { name: editTarget.name, country: editTarget.country ?? '', timeZone: editTarget.timeZone ?? '' } : undefined}
        onSubmit={handleUpdate}
        isLoading={updateMutation.isPending}
      />

      {/* Deactivate confirmation dialog */}
      <Dialog open={Boolean(deactivateTarget)} onClose={() => setDeactivateTarget(null)}>
        <DialogTitle>Deactivate Lab</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivate <strong>{deactivateTarget?.name}</strong>? Existing test data, audit
            logs, and patient records are preserved. This lab will be removed from the lab
            switcher for all users.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeactivate}
            disabled={deactivateMutation.isPending}
          >
            {deactivateMutation.isPending ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
